// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, HostBinding } from '@angular/core';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMFormService } from '@form/services/lm-form.service';
import { LMFormManualRepService } from '@form/lm-form-manual-rep.service';
import { LMDashboardPanelBaseComponent } from './lm-dashboard-panel-base.component';
import { Observer } from 'rxjs';
import { LMHtmlUrlDocComponent } from '@html/lm-html-url-doc.component';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMContextService } from '@services/lm-context.service';
import { AppConstants } from '@utils/app.constants';
import { LMGlobalPersonalizationService } from '@app/states';

@Component({
  selector: 'lm-dashboard-panel-url',
  template: `
    <div soho-widget
        widgetHeight="{{panel.widgetHeight}}"
        widgetWidth="{{panel.widgetWidth}}"
        attr.data-automation-id="lm-{{panel.automationId}}-widget"
    >
      <div soho-widget-header>
        <h2
            soho-widget-title
            tabIndex="0"
            [ngStyle]="{'display': 'inline-block'}"
            [ngClass]="{'hasOverviewClass': (hasOverviewNavigation && displayLinkAsText) }"
            attr.id="{{panel.id}}_widget_title"
            attr.data-automation-id="lm-{{panel.automationId}}-widget-title"
        ><lm-widget-title [title]="panel.title"></lm-widget-title></h2>
        <div class="panelNavigation" [ngClass]="{'hasOverviewClass': hasOverviewNavigation }">
          <button *ngIf="hasOverviewNavigation && !displayLinkAsText"
              soho-button="icon"
              icon="right-arrow"
              soho-tooltip [content]="overNavigationLink"
              (click)="onNavigationClick($event)"
              attr.id="{{panel.id}}_overview_navigation"
              attr.data-automation-id="lm-{{panel.automationId}}-overview-navigation"
          ></button>
          <a *ngIf="hasOverviewNavigation && displayLinkAsText"
              soho-hyperlink
              attr.aria-label="{{panel.title}} - {{overNavigationLink}}"
              [attr.tabindex]="isInAccessibilityMode ? 0 : null"
              class="lm-hyperlink"
              soho-tooltip [content]="overNavigationLink"
              (click)="onNavigationClick($event)"
              (keydown.enter)="onNavigationClick($event)"
              attr.id="{{panel.id}}_overview_navigation"
              attr.data-automation-id="lm-{{panel.automationId}}-overview-navigation"
          >{{overNavigationLink}}</a>
        </div>
      </div>

      <div soho-widget-content>
        <lm-html-url-doc *ngIf="url" #LMHtmlUrlDoc
            [isHtmlFragment]="true"
            [parentContainerId]="!testVersion1 ? panel.id : null"
            [url]="url"
            [isDashboardPanel]=true
            (htmlUrlDisplayed)="onHtmlUrlDisplayed($event)"
            (landmarkCustomReady)="onLandmarkCustomReady($event)"
            [automationId]="this.panel.automationId"
        ></lm-html-url-doc>
      </div>
    </div>`
  ,
  styleUrls: [ './lm-dashboard-panel-url.scss' ],
  providers: [ LMFormService, LMFormManualRepService ]
  })
export class LMDashboardPanelUrlComponent extends LMDashboardPanelBaseComponent implements OnInit {
  @HostBinding('attr.id') public id;
  @HostBinding('attr.data-automation-id') public automationId;

  @ViewChild('LMHtmlUrlDoc') htmlDocComponent: LMHtmlUrlDocComponent;

  @Input() panel: LandmarkPanel;

  // @Input() set panel(panel: LandmarkPanel) {
  //   this._panel = panel;
  //   this.title = panel.title;
  //   this.setUrl();
  //   if (this._panel.overviewNavigation) {
  //     this.hasOverviewNavigation = true;
  //     if (this._panel.overviewNavigation.label) {
  //       this.overViewNavigationLabel();
  //     }
  //   }
  // }
  // get panel(): LandmarkPanel {
  //   return this._panel;
  // }
  @Input() set messageRuleView(messageRuleView: LMDataView) {
    this._messageDataView = messageRuleView;
    this.setUrl();
  }

  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView = fromDataView;
    this.setUrl();
    if (this.htmlDocComponent) {
      this.htmlDocComponent.setDataView(this._fromDataView);
    }
  }

  @Output() uiLinkEvent = new EventEmitter<any>();
  @Output() landmarkCustomReady = new EventEmitter<any>();

  public url: string = undefined;
  public title: string = undefined;
  public overNavigationLink: string;
  public hasOverviewNavigation: boolean;
  public displayLinkAsText: boolean;
  public testVersion1: boolean;
  public isInAccessibilityMode: boolean;

  private _messageDataView: LMDataView = null;
  private _fromDataView: LMDataView = null;

  constructor(
    private lmContextService: LMContextService,
    private lmFormatUtilService: LMFormatUtilService,
    protected lmThemeService: LMThemeService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService
  ) {
    super(lmThemeService);
  }

  ngOnInit() {
    this.isInAccessibilityMode = this.lmGlobalPersonalizationService.getAccessibilityMode();
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;

    this.id = this.panel.id + AppConstants.ELEMENT_ID_SUFFIX_DASHBOARDPANEL;
    this.automationId = `lm-${this.panel.automationId}-dashboardpanel`;
    this.title = this.panel.title;

    this.setUrl();

    if (this.panel.overviewNavigation) {
      this.hasOverviewNavigation = true;
      if (this.panel.overviewNavigation.label) {
        this.displayLinkAsText = true;
        this.overViewNavigationLabel();
      }
    }
  }

  overViewNavigationLabel() {
    const dataView = this.fromDataView ? this.fromDataView : this._messageDataView;
    this.overNavigationLink = this.lmFormatUtilService.formatMessageRule(this.panel.overviewNavigation.label, dataView);
  }

  onNavigationClick(event: any): void {
    const displayField: LMListDisplayField = {
      columnId: '',
      fieldId: '',
      type: '',
      navigation: this.panel.overviewNavigation.name
    };
    const uiLink: LMUILinkEvent = {
      columnDef: { displayField },
      dataView: this._fromDataView,
      event,
      options: { fromDashboard: true }
    };

    this.uiLinkEvent.emit(uiLink);
  }

  onLandmarkCustomReady(event: any) {
    if (this.htmlDocComponent) {
      this.htmlDocComponent.setDataView(this._fromDataView);
    }
  }

  setUrl() {
    if (this.panel) {
      // We need to make sure we have data before calling the formatMessageRule method if we have values to set in a message rule.
      // We only set the url that triggers the template to render if we have both variables and data.
      const dataView = this._fromDataView ? this._fromDataView : this._messageDataView;
      // if ((typeof this.panel.url === 'string')
      //   || (typeof this.panel.url !== 'string' && !this.panel.url.variables)
      //   || (typeof this.panel.url !== 'string' && this.panel.url.variables.length === 0)
      //   || (typeof this.panel.url !== 'string' && this.panel.url.variables.length > 0 && dataView)
      // ) {
      this.url = this.lmFormatUtilService.formatMessageRule(this.panel.url, dataView);
      // }
    }
  }

  onHtmlUrlDisplayed(event: any) {
    // NOOP
  }

  saveDashboardPanelState(): void {
    // NOOP
  }

  clearDashboardPanelState(): void {
    // NOOP
  }

  saveDetailSectionState() {
    // noop
  }

  clearDetailSectionState() {
    // noop
  }

  save(callBack: Observer<boolean>) {
    // nothing to save
    callBack.next(true);
    callBack.complete();
  }
}
